import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { CheckCircle2, Info, AlertCircle, Quote, ArrowRight, Plus, X, Brain, Zap, Heart, Shield, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const BorderlineAccordionItem = ({ title, icon: Icon, children, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDCE] overflow-hidden shadow-sm h-fit">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-[#F4EFE7]/30 transition-colors group cursor-pointer"
      >
        <span className="text-xl font-serif text-[#123F13] flex items-center gap-3">
          <Icon className="text-[#8C2F12] w-6 h-6" /> {title}
        </span>
        <div className="text-[#3B352F]/40 transition-transform duration-300">
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t border-[#E8DDCE]/50">
              <div className="pt-4">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Borderline = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (value) => {
    setOpenItems(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "Borderline behandling i København | Metakognitiv Terapi & Hypnose",
    "description": "Få hjælp til borderline (BPD). Effektiv behandling af følelsesmæssige udsving, tomhed og usikkerhed med metakognitiv terapi og hypnose i Valby.",
    "medicalAudience": "Patient",
    "relevantSpecialty": "Psychiatry",
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "Borderline personlighedsforstyrrelse",
      "alternateName": "BPD",
      "possibleTreatment": [
        {
          "@type": "MedicalTherapy",
          "name": "Metakognitiv Terapi"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Hypnose"
        }
      ]
    }
  };

  return (
    <ServiceLayout 
      title="Borderline behandling – find ro i det indre kaos"
      subtitle="Mærker du følelsesmæssige udsving, tomhed og usikkerhed i dine relationer? Få hjælp til at skabe indre stabilitet og forstå dine egne mønstre."
      description="Borderline personlighedsforstyrrelse (BPD) påvirker måden, du tænker, føler og forholder dig til andre mennesker på. Mange beskriver det som et følelsesmæssigt kaos, hvor stemninger og tanker skifter hurtigt."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/a1775e7f-bf07-41d2-8694-7f16c978dc23.png"
      metaDescription="Specialiseret behandling af borderline (BPD). Lær at håndtere følelseskaos og styrk dine relationer med metakognitiv terapi og hypnose i Valby eller online."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
      footer={
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at få ro og kontrol igen?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      }
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Case Section */}
      <div className="mb-20 bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Det var som om, jeg ikke havde nogen bremse”</h3>
          <div className="text-white/80 mb-6 leading-relaxed space-y-4">
            <p>Lonnie kunne være euforisk om morgenen og helt ude af mig selv om aftenen. Alt føltes for meget.</p>
            <p>Efter et forløb med metakognitiv terapi og hypnose lærte hun at identificere de tankemønstre, der drev hendes følelsesmæssige reaktioner. Hun begyndte at stole på, at hun godt kunne rumme sine følelser.</p>
          </div>
          <p className="font-bold">— Lonnie, 24 år</p>
        </div>
      </div>

      {/* Hvad er Borderline? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Når alting føles for intenst</h2>
        <div className="text-[#3B352F]/70 text-lg leading-relaxed space-y-4">
          <p>
            Hvis du lever med borderline, har dit nervesystem tendens til at reagere meget hurtigt og kraftigt på følelsesmæssige input. Det betyder ikke, at du er overfølsom, men at din krop er i konstant alarmberedskab.
          </p>
          <div className="bg-[#F4EFE7] p-6 rounded-xl border border-[#E8DDCE] mt-8">
            <h4 className="font-bold text-[#123F13] mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-[#8C2F12]" />
              Hvorfor bliver det ved med at gøre ondt?
            </h4>
            <p className="text-sm text-[#3B352F]/80 leading-relaxed">
              Mange kæmper med tankemønstre, der holder fast i smerten gennem konstant analyse af følelser. I behandlingen lærer du at slippe behovet for at analysere alt og i stedet finde ro og klarhed i nuet.
            </p>
          </div>
        </div>
      </div>

      {/* Symptomer */}
      <div className="mb-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-serif text-[#123F13] mb-10 text-center">Typiske symptomer på borderline (BPD)</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          <BorderlineAccordionItem 
            title="Følelser & Relationer" 
            icon={AlertCircle}
            isOpen={openItems.includes('emotions')}
            onClick={() => toggleItem('emotions')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Ekstreme humørsvingninger:</strong> Hurtige skift i stemningsleje.</li>
              <li>• <strong>Frygt for afvisning:</strong> Intens frygt for at blive forladt.</li>
              <li>• <strong>Ustabile relationer:</strong> Skift fra idealisering til nedvurdering.</li>
              <li>• <strong>Følelse af tomhed:</strong> En vedvarende indre meningsløshed.</li>
            </ul>
          </BorderlineAccordionItem>

          <BorderlineAccordionItem 
            title="Adfærd & Identitet" 
            icon={Info}
            isOpen={openItems.includes('identity')}
            onClick={() => toggleItem('identity')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Impulsiv adfærd:</strong> Risikoadfærd i pressede situationer.</li>
              <li>• <strong>Ustabilt selvbillede:</strong> Varierende identitet og værdier.</li>
              <li>• <strong>Intens vrede:</strong> Uhensigtsmæssige udbrud efterfulgt af skam.</li>
              <li>• <strong>Dissociation:</strong> Følelse af at være løsrevet fra virkeligheden.</li>
            </ul>
          </BorderlineAccordionItem>
        </div>
      </div>

      {/* Hvordan behandler jeg borderline? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvordan behandler jeg borderline?</h2>
        <p className="text-[#3B352F]/70 mb-8 text-lg">
          Behandlingen handler ikke om at ændre, hvem du er, men om at give dig redskaberne til at leve et liv med mere frihed og stabilitet.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="bg-[#123F13] text-white p-2 rounded-lg shrink-0">
                <Brain size={24} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">Metakognitiv terapi</h3>
                <p className="text-[#3B352F]/70">
                  Jeg arbejder med dine reaktioner – ikke dine fejl. Du lærer at forholde dig anderledes til dine intense følelser og skabe afstand til destruktive tankemønstre.
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-[#123F13] text-white p-2 rounded-lg shrink-0">
                <Zap size={24} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">Hypnose</h3>
                <p className="text-[#3B352F]/70">
                  Hypnose er et kraftfuldt redskab til at berolige dit nervesystem. Det hjælper dig med at mærke kroppen og skabe tryghed i dig selv.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#F4EFE7] p-8 rounded-2xl border border-[#E8DDCE]">
            <div className="space-y-6 text-[#3B352F]/70">
              <p className="flex gap-3">
                <Shield className="w-5 h-5 text-[#8C2F12] shrink-0 mt-1" />
                <span><strong>Selvregulering:</strong> Du får konkrete værktøjer til at håndtere følelsesudbrud.</span>
              </p>
              <p className="flex gap-3">
                <Heart className="w-5 h-5 text-[#8C2F12] shrink-0 mt-1" />
                <span><strong>Styrkede relationer:</strong> Jeg arbejder med at forstå dine mønstre i relationer.</span>
              </p>
            </div>
            <p className="text-[#123F13] font-serif italic text-lg mt-8">
              “Du lærer at stole på, at du godt kan rumme dine følelser uden at de behøver at styre dig.”
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Link & External Resources */}
      <div className="mb-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="border border-[#E8DDCE] p-8 rounded-2xl hover:bg-[#F4EFE7]/30 transition-colors">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">FAQ - svar på dine spørgsmål</h3>
          <ul className="space-y-2 text-[#3B352F]/70 mb-6">
            <li>• Hvordan foregår behandlingen?</li>
            <li>• Hvor mange gange skal jeg komme?</li>
            <li>• Er det muligt at få det bedre med borderline?</li>
          </ul>
          <Link to="/faq" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
            Se alle spørgsmål og svar <ArrowRight size={16} />
          </Link>
        </div>
        <div className="border border-[#E8DDCE] p-8 rounded-2xl hover:bg-[#F4EFE7]/30 transition-colors">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">Vil du vide mere om borderline?</h3>
          <p className="text-[#3B352F]/70 mb-6">
            Læs om symptomer, forskning og støtte på Bedre Psykiatris hjemmeside.
          </p>
          <a href="https://bedrepsykiatri.dk" target="_blank" rel="noopener noreferrer" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
            Besøg Bedre Psykiatri <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Borderline;
